import { useState, useEffect } from "react";
import { runInAction, transaction } from "mobx";
import { moduleChartStore } from "../../../store/moduleChartStore";
import { inplantsStore } from "../../../features/inplants/store/inplantsStore";
import { isStationVisible } from "../../forms/popupModule/deviceTypeConfig";

interface UseStationsInitResult {
  stationLabels: Record<string, string>;
}

export const useStationsInit = (
  moduleId: string | undefined,
  chartId: string = "stations",
): UseStationsInitResult => {
  const [stationLabels, setStationLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!moduleId) {
      setStationLabels({});
      return;
    }

    const moduleIdNum = Number(moduleId);

    if (inplantsStore.stations.length === 0) {
      setStationLabels({});
      return;
    }

    // `G.2`: у МДБН-9 графики показывают только станции, которые есть в шкафу.
    const cabinetModel = inplantsStore.modules.find(
      (item) => item.id === moduleIdNum,
    )?.cabinet_model;

    const moduleStations = inplantsStore.stations.filter(
      (station) =>
        station.module_id === moduleIdNum &&
        isStationVisible(cabinetModel, station.controller_address),
    );

    const labels: Record<string, string> = {};
    const validKeys = new Set<string>();

    moduleStations.forEach((station) => {
      const key = String(station.controller_address);
      const meterNumber = station["Meters.meter_number"];

      if (!station.place?.trim() || !meterNumber || meterNumber === "0")
        return;

      labels[key] = station.place || meterNumber || `Станция ${key}`;
      validKeys.add(key);
    });

    setStationLabels(labels);

    const saved = localStorage.getItem("activeStations");
    const savedStations = saved ? JSON.parse(saved) : {};

    const merged: Record<string, boolean> = {};
    validKeys.forEach((key) => {
      merged[key] = savedStations[key] ?? true;
    });

    localStorage.setItem("activeStations", JSON.stringify(merged));

    transaction(() => {
      runInAction(() => {
        const current = moduleChartStore.activeSeries[chartId] || {};
        const next = { ...current };
        validKeys.forEach((key) => {
          next[key] = merged[key];
        });
        moduleChartStore.activeSeries = {
          ...moduleChartStore.activeSeries,
          [chartId]: next,
        };
      });
    });
  }, [moduleId, chartId, inplantsStore.stations.length]);

  return { stationLabels };
};
